import {useSelector} from 'react-redux';

import AemButton from '@/components/Aem/AemButton';
import CloseCircleButton from '@/components/CloseButton/CloseCircleButton';
import {AppImage} from '@/components/Image';
import PopupSticked from '@/components/Popup/PopupSticked';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import {useJourneyInfo} from '@/hooks/loyalty/dataHooks';
import appContent from '@/services/content/appContent';
import {formatBadgeImageUrl, getAchievedBadgeImage} from '@/services/loyalty/badgeUtils';
import {selectIsMobileLayout} from '@/state/selectors/global';
import {hideEarnBadgePopup} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';

import LoyaltyTestDesktop from './LoyaltyTestDesktop.png';
import LoyaltyTestMobile from './LoyaltyTestMobile.png';

const EARN_BADGE_POPUP_BADGE_IMAGE_SIZE = 512;

const EarnBadgePopup = ({journeyId}) => {
    const isMobileLayout = useSelector(selectIsMobileLayout);
    const {currentLevelBadge, badgeId, journeyName} = useJourneyInfo(journeyId) || {};
    const {description, button, externalLink} = appContent.getEarnBadgeContent() || {};

    const badgeImage = getAchievedBadgeImage(currentLevelBadge);
    const formattedBadgeImageUrl = formatBadgeImageUrl(
        badgeImage?.imageURL,
        EARN_BADGE_POPUP_BADGE_IMAGE_SIZE,
        EARN_BADGE_POPUP_BADGE_IMAGE_SIZE
    );
    const {redirectToExternalLink: redirect} = useConfigExternalLink({
        ...externalLink,
        ssoParams: {journeyId, badgeId},
    });

    const handleSelectRewardButtonClick = (e) => {
        e.stopPropagation();
        handleClose();
        redirect?.();
    };

    const handleClose = () => {
        dispatch(hideEarnBadgePopup());
    };

    return (
        <PopupSticked
            className='m-0 flex flex-col rounded-none p-0 ml:max-w-max ml:rounded-3xl ml:p-0'
            overlayClassName='bg-charcoal/80 backdrop-blur-lg z-[104] ml:z-[102]'
            onClose={handleClose}
            isCloseVisible={false}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
        >
            <div className='relative flex h-screen w-full bg-primary-dark ml:h-[612px] ml:w-[1024px]'>
                <AppImage
                    className='absolute left-0 top-0 h-full w-full'
                    src={isMobileLayout ? LoyaltyTestMobile : LoyaltyTestDesktop}
                />
                <div className='absolute inset-0 bg-video-overlay ml:hidden' />
                <CloseCircleButton
                    className='absolute right-4 top-14 ml:right-5 ml:top-5'
                    onClick={handleClose}
                    fill='white'
                />
                <div className='flex h-full w-full flex-col items-center justify-center ml:flex-row ml:justify-start'>
                    <div className='absolute left-1/2 top-1/2 h-[302px] w-[302px] -translate-x-1/2 -translate-y-1/2 ml:static ml:left-auto ml:top-auto ml:ml-20 ml:h-[409px] ml:w-[409px] ml:translate-x-0 ml:translate-y-0'>
                        <AppImage
                            className='h-full w-full object-cover'
                            src={formattedBadgeImageUrl}
                            alt={journeyName}
                            decode={false}
                        />
                    </div>
                    <div className='absolute bottom-0 top-[calc(50%+151px)] z-50 flex w-full flex-col ml:static ml:bottom-auto ml:top-auto ml:ml-20 ml:w-auto ml:min-w-[312px] ml:gap-19'>
                        <div className='flex w-full flex-col items-center gap-3 px-10 text-center text-white'>
                            <h1 className='m-0 text-28 font-bold tracking-[-0.39px]'>{journeyName}</h1>
                            <p className='text-12 uppercase leading-12 tracking-[0.2em]'>{description}</p>
                        </div>
                        <AemButton
                            primary
                            dark
                            className='mx-4 mt-auto w-auto mb-safe-offset-8 ml:mx-0 ml:mb-0 ml:mt-0 ml:w-full'
                            onClick={handleSelectRewardButtonClick}
                            {...button}
                        />
                    </div>
                </div>
            </div>
        </PopupSticked>
    );
};

export default EarnBadgePopup;
